// Photos attached to a note: a business card, a whiteboard, a page of
// handwriting. Read as base64 for the draft endpoint; anything larger than
// about 1.5 MB is scaled down first so a phone photo does not become a
// six-megabyte request.
export const MAX_IMAGES = 4;
export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const TARGET_BYTES = 1_500_000;
const MAX_SIDE = 1600;

let seq = 0;

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error || new Error('Could not read the image'));
  reader.readAsDataURL(file);
});

const split = (dataUrl) => {
  const comma = dataUrl.indexOf(',');
  const media = /^data:([^;]+);/.exec(dataUrl.slice(0, comma));
  return { media_type: media ? media[1] : 'image/jpeg', data: dataUrl.slice(comma + 1) };
};

function downscale(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(dataUrl); return; }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => reject(new Error('Could not decode the image'));
    img.src = dataUrl;
  });
}

/** @returns {Promise<{ id, name, media_type, data, previewUrl }>} */
export async function fileToAttachment(file) {
  if (!IMAGE_TYPES.includes(file.type)) throw new Error(`${file.name || 'That file'} is not a JPEG, PNG, WebP or GIF`);
  let dataUrl = await readAsDataUrl(file);
  if (file.size > TARGET_BYTES && file.type !== 'image/gif') dataUrl = await downscale(dataUrl);
  const { media_type, data } = split(dataUrl);
  seq += 1;
  return { id: `img-${seq}`, name: file.name || `image ${seq}`, media_type, data, previewUrl: dataUrl };
}

/** Image files from a drop or paste event, if any. */
export function imageFilesFrom(dataTransfer) {
  if (!dataTransfer) return [];
  const files = [];
  if (dataTransfer.items) {
    for (const item of dataTransfer.items) {
      if (item.kind === 'file') { const f = item.getAsFile(); if (f && IMAGE_TYPES.includes(f.type)) files.push(f); }
    }
  } else if (dataTransfer.files) {
    for (const f of dataTransfer.files) if (IMAGE_TYPES.includes(f.type)) files.push(f);
  }
  return files;
}
